import { PaginationFintechInterface } from '@/types/cashAdvanceInterface';
import {
  ActiveRowTransactionHistoryInterface,
  ReadyWageTransactionHistoryDetailsInterface,
  ReadyWageTransactionHistoryTableDataInterface,
  ReadyWageCreditComputationDataInterface,
  ReadyWageCompanySettingsInterface,
  ReadyWageBalanceBreakdownInterface,
  ReadyWageAttendanceDataInterface,
  ReadyWageAdjustmentsDataInterface,
  ReadyWageBankDetailsInterface,
  ReadyWageGetWageInput,
  ReadyWageConvenienceFeeDataInterface,
  TransactionHistoryDetailsInterface,
} from '@/types/instaWageInterface';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { GetTransactionHistoryDetails } from '@/services/ReadyWageHome/GetTransactionHistoryDetails';
import { GetCreditComputation } from '@/services/ReadyWageHome/GetCreditComputation';
import { GetBankDetails } from '@/services/ReadyWageHome/GetBankDetails';

export const useReadyWageHomeStore = defineStore('readyWageHomeStore', () => {
  const isReadyWageHomeOn = ref<boolean>(false);
  const isTeaserModalVisible = ref<boolean>(false);
  const isOnboardingModalVisible = ref<boolean>(false);
  const isTermsAndConditionsVisible = ref<boolean>(false);
  const isTransactionListLoading = ref<boolean>(false);
  const isTransactionListDetailsLoading = ref<boolean>(false);
  const isCardLoading = ref<boolean>(false);
  const showErrorSalaryCard = ref<boolean>(false);
  const errorMessage = ref<string>('');
  const hasReadyWage = ref<boolean>(false);
  const isReadyWageAdmin = ref<boolean>(false);
  const isAmountSelectionOpen = ref<boolean>(false);
  const readyWageHomeLoader = ref<boolean>(false);
  const isBalanceBreakdownLoading = ref<boolean>(false);
  const isCancelTransactionModalVisible = ref<boolean>(false);
  const nextRoute = ref<object>(null);
  const isBalanceBreakdownAccordionOpen = ref<{ [key: string]: boolean }>({});
  const isAccountNumberVisible = ref<boolean>(false);
  const isServerErrorModalWithRefVisible = ref<boolean>(false);
  const isServerErrorModalNoRefVisible = ref<boolean>(false);
  const serverErrorReferenceKey = ref<string>('');
  const isMaintenanceModalVisible = ref<boolean>(false);
  const isCreditComputationLoading = ref(false);
  const isReadyWageHomeComputeLateandUTOn = ref<boolean>(false);
  const closeSmallScreenBanner = ref<boolean>(false);
  const LD_ReadyWage_PartnerDowntime = ref<boolean>(false);

  const getWageInput = ref<ReadyWageGetWageInput>({
    amount: null,
    note: '',
  });

  const showCompletionPage = ref<boolean>(false);
  const completionPageLoader = ref<boolean>(false);

  const completionPageDetails = ref<TransactionHistoryDetailsInterface>({
    transactionId: null,
    bankName: '',
    accountNumber: '',
    recipientName: '',
    transactionStatus: '',
    amount: 0,
    convenienceFee: 0,
    totalAmount: 0,
    transferNote: '',
    dateCredited: '2023-09-07T11:42:33.177Z',
    dateRequested: '2023-09-07T11:42:33.177Z',
    affectedPayslipDate: '2023-09-07T11:42:33.177Z',
    referenceCode: '',
  });

  const paginationFintech = ref<PaginationFintechInterface>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });
  const showTransactionListLoading = () => {
    isTransactionListLoading.value = true;
  };

  const hideTransactionListLoading = () => {
    isTransactionListLoading.value = false;
  };

  const changePage = async (paginate: PaginationFintechInterface) => {
    paginationFintech.value.currentPage = paginate.currentPage;
    paginationFintech.value.itemsPerPage = paginate.itemsPerPage;
    await getTransactionHistoryTable();
  };
  const activeRow = ref<ActiveRowTransactionHistoryInterface>({
    index: null,
    details: {
      transactionId: 0,
      bankName: 'string',
      accountNumber: 'string',
      recipientName: 'string',
      transactionStatus: 'string',
      amount: 0,
      convenienceFee: 0,
      totalAmount: 0,
      transferNote: 'string',
      dateCredited: '2023-09-07T11:42:33.177Z',
      dateRequested: '2023-09-07T11:42:33.177Z',
      affectedPayslipDate: 'string',
      referenceCode: 'string',
    },
  });
  const readyWageHomeTransactionListDetails =
    ref<ReadyWageTransactionHistoryDetailsInterface>({
      state: false,
      isLoading: false,
    });

  const readyWageHomeBalanceBreakdown = ref<ReadyWageBalanceBreakdownInterface>(
    {
      state: false,
      isLoading: false,
    }
  );

  const transactionTableData =
    ref<ReadyWageTransactionHistoryTableDataInterface>({
      header: {
        clientId: 0,
        companyId: 0,
        clientName: '',
        companyName: '',
        outstandingBalance: 0,
        pending: 0,
        inProgress: 0,
        success: 0,
        paid: 0,
        failed: 0,
        totalRequests: 0,
        deducted: 0,
      },
      data: [],
      pagination: {
        currentPage: 0,
        totalPages: 0,
        totalItems: 0,
        itemsPerPage: 0,
        links: {
          next: 'string',
          previous: 'string',
          last: 'string',
        },
      },
    });

  const creditComputationData = ref<ReadyWageCreditComputationDataInterface>({
    dailyRate: 0,
    minuteRate: 0,
    daysWorked: 0,
    dailyHour: 0,
    companyWorkHoursPerDay: 0,
    accessRate: 0,
    cutOffDateFrom: '2024-09-27T08:12:03.333Z',
    cutOffDateTo: '2024-09-27T08:12:03.333Z',
    payOutDate: '2024-09-27T08:12:03.333Z',
    totalPreviousInstaWage: 0,
    availableCredit: 0,
    isInstaCashActivated: false,
    totalInstaCashOutstandingBalance: 0,
    balanceSubtotal: 0,
  });

  const bankDetailsData = ref<ReadyWageBankDetailsInterface>({
    bankName: '',
    accountNumber: '',
    bankCodeId: 0,
  });

  const companySettingsData = ref<ReadyWageCompanySettingsInterface>({
    accessRate: 0,
    convenienceFee: 0,
    companyStatus: '',
    enableWithDraw: false,
    minimumAdvanceAmount: 0,
    maximumAdvanceAmount: 0,
  });

  const attendanceBreakdownData = ref<ReadyWageAttendanceDataInterface[]>([
    {
      date: '2024-09-16T00:00:00',
      onLeave: false,
      isHoliday: false,
      shiftSchedule: '09:00 AM To 06:00 PM',
      daysAbsent: 0,
      daysPresent: 1,
      lateMinutes: 0,
      undertimeMinutes: 0,
    },
  ]);

  const adjustmentsData = ref<ReadyWageAdjustmentsDataInterface>({
    totalAdjustments: 0,
    totalLateMinutes: 0,
    totalLateAmount: 0,
    totalUndertimeMinutes: 0,
    totalUndertimeAmount: 0,
  });

  const convenienceFeeData = ref<ReadyWageConvenienceFeeDataInterface>({
    amount: 0,
    convenienceFee: 0,
    totalAmount: 0,
  });

  const handleErrorResponse = (error: {
    response?: { data?: { referenceKey?: string }; status?: number };
  }) => {
    console.error(error);

    const referenceKey = error?.response?.data?.referenceKey;
    const errorStatus = error?.response?.status;

    if (errorStatus >= 400) {
      if (referenceKey) {
        serverErrorReferenceKey.value = referenceKey;
        isServerErrorModalWithRefVisible.value = true;
      } else {
        isServerErrorModalNoRefVisible.value = true;
      }
    }
  };

  const getTransactionHistoryTable = async () => {
    readyWageHomeLoader.value = true;
    try {
      const { currentPage, itemsPerPage } = paginationFintech.value;

      const response = await GetTransactionHistoryDetails({
        PageNumber: currentPage,
        RowsPerPage: itemsPerPage,
      });
      if (typeof response !== 'object') {
        return;
      }
      transactionTableData.value = response.data;
      Object.assign(paginationFintech.value, response.data.pagination);
    } catch (error) {
      handleErrorResponse(error);
    } finally {
      readyWageHomeLoader.value = false;
    }
  };

  const getCreditComputation = async () => {
    isCardLoading.value = true;
    try {
      const response = await GetCreditComputation();
      creditComputationData.value = response.data;
      attendanceBreakdownData.value = response.data.attendanceBreakdown;
      adjustmentsData.value = response.data.adjustments;

      checkConditions();
    } catch (error) {
      handleErrorResponse(error);
    } finally {
      isCardLoading.value = false;
    }
  };

  const checkConditions = () => {
    const { availableCredit } = creditComputationData.value;
    const { enableWithDraw, minimumAdvanceAmount } = companySettingsData.value;

    if (!LD_ReadyWage_PartnerDowntime.value) {
      if (enableWithDraw === false) {
        showErrorSalaryCard.value = true;
        errorMessage.value =
          'Withdrawals are disabled at the moment, please contact your HR for more information.';
      } else if (availableCredit < 0) {
        showErrorSalaryCard.value = true;
        errorMessage.value = `Sorry! Your <b>ReadyWage Balance</b> is below the ${minimumAdvanceAmount.toLocaleString(
          'en-US'
        )} PHP minimum due to your <b>ReadyCash</b>. You can transact again once your ReadyWage Balance exceeds your minimum.`;
      } else if (availableCredit < minimumAdvanceAmount) {
        showErrorSalaryCard.value = true;
        errorMessage.value = `Sorry! Your <b>ReadyWage Balance</b> doesn’t meet the minimum amount of ${minimumAdvanceAmount.toLocaleString(
          'en-US'
        )} PHP.`;
      } else {
        showErrorSalaryCard.value = false;
      }
    }
  };

  const getBankDetails = async () => {
    try {
      const response = await GetBankDetails();
      bankDetailsData.value = response.data;
    } catch (error) {
      handleErrorResponse(error);
    }
  };

  const toggleAccordion = (accordionName: string) => {
    isBalanceBreakdownAccordionOpen.value[accordionName] =
      !isBalanceBreakdownAccordionOpen.value[accordionName];
  };

  const resetAccordion = () => {
    Object.keys(isBalanceBreakdownAccordionOpen.value).forEach((key) => {
      isBalanceBreakdownAccordionOpen.value[key] = false;
    });
  };

  const toggleBalanceBreakdown = () => {
    readyWageHomeTransactionListDetails.value.state = false;
    readyWageHomeBalanceBreakdown.value.state =
      !readyWageHomeBalanceBreakdown.value.state;
    readyWageHomeBalanceBreakdown.value.isLoading = true;
    activeRow.value.index = null;

    resetAccordion();
    setTimeout(() => {
      readyWageHomeBalanceBreakdown.value.isLoading = false;
    }, 2000);
  };

  const detailsButtonText = computed(() => {
    return readyWageHomeBalanceBreakdown.value.state
      ? 'Hide Details'
      : 'View Details';
  });

  return {
    isReadyWageHomeOn,
    isTransactionListLoading,
    transactionTableData,
    showTransactionListLoading,
    hideTransactionListLoading,
    changePage,
    paginationFintech,
    readyWageHomeTransactionListDetails,
    activeRow,
    isTeaserModalVisible,
    isOnboardingModalVisible,
    isTermsAndConditionsVisible,
    getTransactionHistoryTable,
    isCardLoading,
    isTransactionListDetailsLoading,
    getCreditComputation,
    creditComputationData,
    showErrorSalaryCard,
    errorMessage,
    companySettingsData,
    hasReadyWage,
    isReadyWageAdmin,
    isAmountSelectionOpen,
    readyWageHomeBalanceBreakdown,
    attendanceBreakdownData,
    toggleBalanceBreakdown,
    detailsButtonText,
    isBalanceBreakdownLoading,
    adjustmentsData,
    getBankDetails,
    bankDetailsData,
    getWageInput,
    convenienceFeeData,
    isCancelTransactionModalVisible,
    nextRoute,
    isBalanceBreakdownAccordionOpen,
    toggleAccordion,
    resetAccordion,
    showCompletionPage,
    isAccountNumberVisible,
    isServerErrorModalWithRefVisible,
    isServerErrorModalNoRefVisible,
    serverErrorReferenceKey,
    completionPageDetails,
    completionPageLoader,
    isMaintenanceModalVisible,
    isCreditComputationLoading,
    checkConditions,
    handleErrorResponse,
    isReadyWageHomeComputeLateandUTOn,
    closeSmallScreenBanner,
    LD_ReadyWage_PartnerDowntime,
  };
});
